/**
 * useRealTimeTracking — WebSocket orqali real-time GPS kuzatuv hook.
 *
 * Ishlatilishi:
 *   const { lastLocation, events, connected } = useRealTimeTracking(token);
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const WS_BASE = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

export default function useRealTimeTracking(token) {
  const [connected, setConnected] = useState(false);
  const [lastLocations, setLastLocations] = useState({}); // { device_id: locationData }
  const [events, setEvents] = useState([]); // Oxirgi hodisalar
  const [devices, setDevices] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);

  const connect = useCallback(() => {
    if (!token) return;

    const wsUrl = `${WS_BASE}//${window.location.host}/ws/track/?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      // Ping har 30 soniyada — ulanish tirik turadi
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);

        switch (data.type) {
          case 'connected':
            setDevices(data.devices || []);
            break;

          case 'location':
            setLastLocations(prev => ({
              ...prev,
              [data.device_id]: {
                latitude: data.latitude,
                longitude: data.longitude,
                speed: data.speed,
                altitude: data.altitude,
                accuracy: data.accuracy,
                battery: data.battery,
                address: data.address,
                child_name: data.child_name,
                timestamp: data.timestamp,
              }
            }));
            break;

          case 'zone_event':
          case 'signal_event':
          case 'battery_event':
            setEvents(prev => [{
              ...data,
              id: Date.now(),
              receivedAt: new Date().toISOString(),
            }, ...prev].slice(0, 50));
            break;

          case 'pong':
            break;

          default:
            break;
        }
      } catch (err) {
        console.error('WS parse error:', err);
      }
    };

    ws.onclose = (e) => {
      setConnected(false);
      clearInterval(pingTimer.current);

      // Avtomatik qayta ulanish (3 soniyadan keyin)
      if (e.code !== 4001) { // 4001 = auth xato
        reconnectTimer.current = setTimeout(() => connect(), 3000);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token]);

  useEffect(() => {
    connect();

    return () => {
      clearTimeout(reconnectTimer.current);
      clearInterval(pingTimer.current);
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Qurilmalar ro'yxatini yangilash
  const refresh = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'refresh' }));
    }
  }, []);

  return {
    connected,
    lastLocations,
    events,
    devices,
    refresh,
  };
}
